import React from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Table, Spinner } from "reactstrap";

export default function DataTable({
  data,
  columns,
  total = 0,
  pagination,
  setPagination,
  selectedRows = [],
  onRowSelect,
  onDelete,
  progressPending,
}) {
  const table = useReactTable({
    data,
    columns,
    pageCount: Math.ceil(total / pagination.pageSize),
    state: { pagination },
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange: setPagination,
  });

  const handleRowClick = (row) => {
    if (!onRowSelect) return;
    const isSelected = selectedRows.some((r) => r.id === row.original.id);
    if (isSelected) {
      onRowSelect(selectedRows.filter((r) => r.id !== row.original.id));
    } else {
      onRowSelect([...selectedRows, row.original]);
    }
  };

  return (
    <div>
      {/* Barra de acciones de selección */}
      {selectedRows?.length > 0 && (
        <div className="d-flex align-items-center justify-content-between bg-light-info p-2 mb-2 rounded shadow-sm">
          <h6 className="text-muted mb-0">
            {selectedRows.length} registros seleccionados
          </h6>
          <button onClick={onDelete} className="btn btn-danger btn-sm">
            Eliminar seleccionados
          </button>
        </div>
      )}

      {/* Tabla */}
      <div className="table-responsive">
        <Table bordered hover className="table-striped align-middle text-center">
          <thead className="table-light">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {progressPending ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-4">
                  <Spinner color="primary" />
                  <p className="mt-2 mb-0">Cargando datos...</p>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center">
                  No hay registros
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => {
                const isSelected = selectedRows.some((r) => r.id === row.original.id);
                return (
                  <tr
                    key={row.id}
                    className={`${isSelected ? "table-primary" : ""} cursor-pointer`}
                    onClick={() => handleRowClick(row)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      {/* Paginación */}
      <div className="d-flex justify-content-end align-items-center mt-3 flex-wrap gap-2 small">
        <div className="d-flex align-items-center me-3">
          <span className="me-2">Por página:</span>
          <select
            value={pagination.pageSize}
            onChange={(e) =>
              setPagination({ ...pagination, pageSize: Number(e.target.value), pageIndex: 0 })
            }
            className="form-select form-select-sm"
            style={{ width: "80px" }}
          >
            {[5, 10, 20, 50].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        {/* Rango mostrado */}
        <div className="me-3">
          {total > 0 && (
            <>
              {pagination.pageIndex * pagination.pageSize + 1} -{" "}
              {Math.min((pagination.pageIndex + 1) * pagination.pageSize, total)}{" "}
              de {total}
            </>
          )}
        </div>

        {/* Página actual */}
        <div className="me-3">
          Página {pagination.pageIndex + 1} de {Math.ceil(total / pagination.pageSize)}
        </div>

        {/* Botones navegación */}
        <div className="btn-group btn-group-sm" role="group">
          <button
            onClick={() => setPagination({ ...pagination, pageIndex: 0 })}
            disabled={pagination.pageIndex === 0}
            className="btn btn-outline-secondary"
          >
            {"|<"}
          </button>
          <button
            onClick={() => setPagination({ ...pagination, pageIndex: pagination.pageIndex - 1 })}
            disabled={pagination.pageIndex === 0}
            className="btn btn-outline-secondary"
          >
            {"<"}
          </button>
          <button
            onClick={() => setPagination({ ...pagination, pageIndex: pagination.pageIndex + 1 })}
            disabled={(pagination.pageIndex + 1) * pagination.pageSize >= total}
            className="btn btn-outline-secondary"
          >
            {">"}
          </button>
          <button
            onClick={() =>
              setPagination({
                ...pagination,
                pageIndex: Math.floor((total - 1) / pagination.pageSize),
              })
            }
            disabled={(pagination.pageIndex + 1) * pagination.pageSize >= total}
            className="btn btn-outline-secondary"
          >
            {">|"}
          </button>
        </div>
      </div>
    </div>
  );
}
